import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { MediaPlaylistDto } from '@/types/dtos/media/media-playlist.dto.ts';
import type { PlaylistNextShowMode } from '@/types/dtos/media/playlist-next-show-mode.enum';
import type { VirtualMediaDisplayAs } from '@/types/dtos/media/virtual-media.dto';
import { patchMediaPlaylistInQueryCache } from '@/state/mutations/media/patch-media-playlist-cache.ts';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

export type PatchPlaylistVirtualMediaDto = {
  enabledSlugs?: string[];
  items?: Record<string, { displayAs?: VirtualMediaDisplayAs; nextShowMode?: PlaylistNextShowMode }>;
};

export const usePatchPlaylistVirtualMediaMutation = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({ playlistId, ...body }: PatchPlaylistVirtualMediaDto & { playlistId: string }) => {
      const token = await getToken();
      return patchPlaylistVirtualMedia(playlistId, body, token ?? '');
    },
    onSuccess: (updatedPlaylist) => {
      patchMediaPlaylistInQueryCache(queryClient, updatedPlaylist);
      void invalidateSimulationCueSheet(queryClient);
    },
  });
};

const patchPlaylistVirtualMedia = async (
  playlistId: string,
  body: PatchPlaylistVirtualMediaDto,
  token: string,
): Promise<MediaPlaylistDto> => {
  const url = `${import.meta.env.VITE_API_URL}/media-playlists/${playlistId}/virtual-media`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body, token });
};
